import { useState, useRef, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { ALL_VANILLA_ENTRANCES } from '../data/vanilla-entrances';
import { ZONE_TO_COLOR_REGION, ENTRANCE_CATEGORY_COLORS } from '../data/entrance-types';
import { AREA_COLORS } from '@/components/tracker/data/area-colors';

interface DestinationPickerProps {
  currentValue: string | null;
  onSelect: (destination: string, from?: string | null) => void;
  onClear: () => void;
  onClose: () => void;
}

/** Build a list of all possible destinations grouped by zone */
const ALL_DESTINATIONS = (() => {
  const map = new Map<string, { zone: string; label: string; vanillaTo: string; category: string }>();
  for (const ent of ALL_VANILLA_ENTRANCES) {
    const key = `${ent.zone}::${ent.label}`;
    if (!map.has(key)) {
      map.set(key, { zone: ent.zone, label: ent.label, vanillaTo: ent.vanillaTo, category: ent.category });
    }
  }
  return Array.from(map.values());
})();

export function DestinationPicker({ currentValue, onSelect, onClear, onClose }: DestinationPickerProps) {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return ALL_DESTINATIONS.slice(0, 30);
    return ALL_DESTINATIONS.filter(
      d =>
        d.label.toLowerCase().includes(term) ||
        d.zone.toLowerCase().includes(term) ||
        d.vanillaTo.toLowerCase().includes(term)
    ).slice(0, 30);
  }, [search]);

  return (
    <div
      className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-64 max-h-80 flex flex-col"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 p-2 border-b border-gray-700">
        <input
          ref={inputRef}
          type="text"
          className="flex-1 bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-600 outline-none focus:border-zelda-gold"
          placeholder="Search destination..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter' && filtered.length > 0) {
              onSelect(filtered[0].vanillaTo);
            }
          }}
        />
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={14} />
        </button>
      </div>

      {currentValue && (
        <div className="px-2 py-1 border-b border-gray-700 flex items-center justify-between">
          <span className="text-[10px] text-gray-400">
            Current: <span className="text-white">{currentValue}</span>
          </span>
          <button
            onClick={onClear}
            className="text-[10px] text-red-400 hover:text-red-300"
          >
            Clear
          </button>
        </div>
      )}

      <div className="overflow-y-auto flex-1">
        {filtered.map((d) => {
          const region = ZONE_TO_COLOR_REGION[d.zone];
          const zoneColor = region ? AREA_COLORS[region] : '#6b7280';
          const catColor = ENTRANCE_CATEGORY_COLORS[d.category as keyof typeof ENTRANCE_CATEGORY_COLORS];

          return (
            <button
              key={`${d.zone}::${d.label}`}
              className="w-full text-left px-2 py-1.5 hover:bg-gray-800 flex items-center gap-2 text-[11px]"
              onClick={() => onSelect(d.vanillaTo)}
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: catColor }}
              />
              <span style={{ color: zoneColor }} className="font-medium flex-shrink-0">
                {d.zone}
              </span>
              <span className="text-gray-300 truncate">{d.label}</span>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-2 py-4 text-center text-gray-500 text-xs">No results</div>
        )}
      </div>
    </div>
  );
}
